/**
 * The typed API client — requirements 3.1, 3.2's client half, and 3.5.
 *
 * Every response is parsed through a schema from `@apuestapr/contracts` before the app sees it, and
 * the result is a discriminated union rather than a thrown exception or a partially-typed object. The
 * point is requirement 3.5: a response that does not match its contract has to produce an **error
 * state**, not a render of `undefined`. A client that returned `T` and trusted the wire would satisfy
 * the type checker and put `undefined` in a heading, which is the failure mode the whole contracts
 * package exists to remove — the type would say the field is there and only a screenshot would
 * disagree.
 *
 * `ok: false` cannot be ignored by accident: there is no `data` on that branch, so a caller that
 * forgets to check it does not compile.
 *
 * ## Three failures, kept apart
 *
 * They are three different bugs and a reader needs to know which one happened: the API was
 * unreachable, the API answered with an error envelope, or the API answered with a body that is not
 * what the contract says. Collapsing them into one "something went wrong" is how a version skew
 * between Pages and Railway gets diagnosed as a network problem.
 *
 * ## Credentials are opt-in, and only one app opts in
 *
 * `credentials` is off by default, so a client built without thinking about it attaches nothing. The
 * dashboard turns it on in `admin-api.ts` because the session cookie Task 12 issues is what
 * authenticates it; the site's `public-api.ts` leaves it off, and a test asserts that it stays off.
 * That is where the difference between the two apps belongs — this file is byte-identical in both, so
 * a policy decision cannot live here.
 *
 * The credential itself is never visible to this code. It is an `httpOnly` cookie, so `fetch` sends it
 * and nothing in JavaScript can read it, which is requirement 5.2. What the client does hold is the
 * CSRF token, which is not a credential: it is the header half of a double-submit pair and it
 * authorises nothing without the cookie. It is passed per call rather than stored here, so the one
 * place it lives is the component state that received it.
 *
 * **This file is byte-identical in `apps/web` and `apps/dashboard`, and a test asserts that** — see
 * the note in `base-url.ts` for why the duplication is deliberate and how it is kept honest.
 */
import { apiErrorSchema, type ErrorCode, type FieldError } from '@apuestapr/contracts';
import type { ZodType } from 'zod';

export type ApiFailure =
  /** The request never got an answer: DNS, TLS, a refused connection, an aborted fetch. */
  | { readonly kind: 'unreachable'; readonly message: string }
  /**
   * The API answered with a non-2xx status. `code` and `requestId` are present when the body was the
   * error envelope, which is what makes a failure traceable to a log line — and absent when it was
   * not, because an intermediary (a proxy, a CDN error page) can answer instead of the API.
   */
  | {
      readonly kind: 'http';
      readonly status: number;
      readonly code: ErrorCode | undefined;
      readonly requestId: string | undefined;
      readonly message: string;
      /** Field-addressable validation/conflict details retained for forms. */
      readonly details?: readonly FieldError[];
    }
  /** The body was not JSON, or was JSON that does not satisfy the contract. */
  | {
      readonly kind: 'malformed';
      readonly message: string;
      readonly issues: readonly FieldError[];
    };

export type ApiResult<T> =
  { readonly ok: true; readonly data: T } | { readonly ok: false; readonly failure: ApiFailure };

/** The methods that carry a body and need a CSRF token. `GET` has its own method below. */
export type UnsafeMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  /** Cancels the underlying fetch and any response parsing when its owner is no longer current. */
  readonly signal?: AbortSignal | undefined;
}

export interface SendOptions extends RequestOptions {
  /** JSON-encoded. Omitted entirely when absent, rather than sent as `null`. */
  readonly body?: unknown;
  /**
   * The double-submit token, from the last session or CSRF response. Sent as `X-CSRF-Token`, which is
   * on the admin surface's CORS allowlist. Absent means the API will refuse the write, which is the
   * correct outcome for a caller that has not fetched one.
   */
  readonly csrfToken?: string | undefined;
}

export interface ApiClient {
  /** GET `path` and parse the body with `schema`. `path` is absolute from the API root. */
  readonly get: <T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ) => Promise<ApiResult<T>>;
  /** An unsafe method, with an optional body and the CSRF token. Same parse-or-fail contract. */
  readonly send: <T>(
    method: UnsafeMethod,
    path: string,
    schema: ZodType<T>,
    options?: SendOptions,
  ) => Promise<ApiResult<T>>;
}

export interface ApiClientOptions {
  /** From `resolveApiBaseUrl`. `''` means same-origin. */
  readonly baseUrl: string;
  /** Injected so every branch below is testable without a network or a mocked global. */
  readonly fetch?: typeof globalThis.fetch;
  /**
   * Send cookies with every request. Off by default and turned on by the dashboard alone.
   *
   * Off is not merely a default here: the public surface rejects a credential presented to it
   * (requirement 5.9) and the public site must never carry one (5.3), so a client that attached
   * cookies "just in case" would be doing the one thing the two-hostname split exists to prevent.
   */
  readonly credentials?: boolean;
}

const describe = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

const readJson = async (
  response: Response,
): Promise<{ readonly parsed: true; readonly value: unknown } | { readonly parsed: false }> => {
  try {
    return { parsed: true, value: (await response.json()) as unknown };
  } catch {
    return { parsed: false };
  }
};

/**
 * Turn a response into a result. Shared by both methods, so a `POST` and a `GET` cannot come to
 * disagree about what a 403 with no envelope means.
 */
export const interpretApiResponse = async <T>(
  response: Response,
  schema: ZodType<T>,
): Promise<ApiResult<T>> => {
  const body = await readJson(response);

  if (!response.ok) {
    const envelope = body.parsed ? apiErrorSchema.safeParse(body.value) : undefined;
    return {
      ok: false,
      failure: {
        kind: 'http',
        status: response.status,
        code: envelope?.success === true ? envelope.data.error.code : undefined,
        requestId: envelope?.success === true ? envelope.data.requestId : undefined,
        message:
          envelope?.success === true
            ? envelope.data.error.message
            : `The API answered ${String(response.status)} with no error envelope.`,
        ...(envelope?.success === true && envelope.data.error.details !== undefined
          ? { details: envelope.data.error.details }
          : {}),
      },
    };
  }

  if (!body.parsed) {
    return {
      ok: false,
      failure: { kind: 'malformed', message: 'The response body was not JSON.', issues: [] },
    };
  }

  const contract = schema.safeParse(body.value);
  if (!contract.success) {
    // The field paths are what makes this actionable: "the response is wrong" sends someone
    // reading a whole payload, "locations.3.nombre: expected string" names the row.
    return {
      ok: false,
      failure: {
        kind: 'malformed',
        message: 'The response did not match its contract.',
        issues: contract.error.issues.map((issue) => ({
          path: issue.path.join('.'),
          message: issue.message,
        })),
      },
    };
  }

  return { ok: true, data: contract.data };
};

export const createApiClient = ({
  baseUrl,
  fetch: fetchImpl = globalThis.fetch,
  credentials = false,
}: ApiClientOptions): ApiClient => {
  const mode: RequestCredentials = credentials ? 'include' : 'omit';

  const get = async <T>(
    path: string,
    schema: ZodType<T>,
    options: RequestOptions = {},
  ): Promise<ApiResult<T>> => {
    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, {
        headers: { accept: 'application/json' },
        credentials: mode,
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
    } catch (cause) {
      return { ok: false, failure: { kind: 'unreachable', message: describe(cause) } };
    }

    return interpretApiResponse(response, schema);
  };

  const send = async <T>(
    method: UnsafeMethod,
    path: string,
    schema: ZodType<T>,
    options: SendOptions = {},
  ): Promise<ApiResult<T>> => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (options.csrfToken !== undefined) headers['x-csrf-token'] = options.csrfToken;

    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, {
        method,
        headers,
        credentials: mode,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
    } catch (cause) {
      return { ok: false, failure: { kind: 'unreachable', message: describe(cause) } };
    }

    return interpretApiResponse(response, schema);
  };

  return { get, send };
};
